import { useState, useEffect } from 'react'
import { useRecoilState, useRecoilValue } from 'recoil'
import clsx from 'clsx'
import BigNumber from 'bignumber.js'
import AssetInputBlock from '@/components/wallet/AssetInputBlock'
import { networkState } from '@/recoil/Network'
import { collateralInfoValue } from '@/recoil/Position'
import contractHelper from '@/api/contractHelper'
import numberFormat from '@/utils/numberFormat'
import { USDC_TOKEN_LIST } from '@/constants/token'
import './WalletOpreationPopup.css'

const ANIMATION_TIME = 200
const DESTINATION_NAME_MAP = {
  defiWallet: 'DeFi Wallet',
  default: 'Trading Wallet'
}

const MAX_SELECTED_ASSET_AMOUNT = 3

export default function WalletOpreationPopup({
  show,
  accountWallet,
  wallet,
  isDeposit = true,
  assetList,
  selectedAssetList,
  setSelectedAssetList = () => {},
  updateWalletBalance = () => {},
  hidePopupCallback = () => {}
}) {
  const [network] = useRecoilState(networkState)
  const { freeCollateral } = useRecoilValue(collateralInfoValue)
  const [isLoading, setIsLoading] = useState(false)
  const [resultMap, setResultMap] = useState({})
  const [requestFreeToken, setRequestFreeToken] = useState(false)
  const [showElement, setShowElement] = useState(false)
  const [active, setActive] = useState(false)
  const isTestnet = network === 'testnet'
  const operation = isDeposit ? 'Deposit' : 'Withdraw'
  const walletMap = isDeposit ? wallet : accountWallet
  const dataKey = isDeposit ? 'balance' : 'available'
  const destinationName = isDeposit
    ? DESTINATION_NAME_MAP.default
    : DESTINATION_NAME_MAP.defiWallet
  const availableAssetAmount =
    MAX_SELECTED_ASSET_AMOUNT - selectedAssetList.length
  const assetText = availableAssetAmount > 1 ? 'assets' : 'asset'
  const showFreeUsdcButton =
    isTestnet && isDeposit && BigNumber(walletMap['USDC.e'][dataKey]).lt(1000)

  const validSelectedAssetList = selectedAssetList.filter(token => {
    const isUsdc = USDC_TOKEN_LIST.includes(token)
    const result = resultMap[token] || 0
    const balance =
      isUsdc && !isDeposit
        ? freeCollateral
        : (walletMap[token] || {})[dataKey] || 0
    return BigNumber(result).gt(0) && BigNumber(balance).gt(0)
  })

  const operationValid =
    !isLoading &&
    ((validSelectedAssetList.length > 0 &&
      validSelectedAssetList.every(token => {
        const isUsdc = USDC_TOKEN_LIST.includes(token)
        const balance =
          isUsdc && !isDeposit
            ? freeCollateral
            : (walletMap[token] || {})[dataKey] || 0
        return BigNumber(resultMap[token]).lte(balance)
      })) ||
      (validSelectedAssetList.length === 0 && requestFreeToken))

  useEffect(() => {
    if (show) {
      setShowElement(true)
      setTimeout(() => setActive(true), 20)
    }
  }, [show])

  const onPopupClose = () => {
    setActive(false)
    setTimeout(() => {
      setShowElement(false)
      hidePopupCallback()
      setSelectedAssetList([])
      setResultMap({})
      setRequestFreeToken(false)
    }, ANIMATION_TIME)
  }

  const onClickAddAsset = () => {
    if (availableAssetAmount <= 0) {
      return
    }
    const token = assetList.find(symbol => !selectedAssetList.includes(symbol))
    token && setSelectedAssetList(prevList => prevList.concat(token))
  }

  const onSubmit = () => {
    if (!operationValid) {
      return
    }
    setIsLoading(true)

    const tokenList = validSelectedAssetList.map(token => ({
      token,
      amount: resultMap[token],
      checkStorageBalance: BigNumber(wallet[token].balance).eq(0)
    }))

    const method = isDeposit ? 'depositTokens' : 'withdrawTokens'
    contractHelper[method](tokenList, requestFreeToken)
      .then(() => {
        updateWalletBalance()
        setResultMap({})
      })
      .finally(() => setIsLoading(false))
  }

  const assetDropdownList = assetList.filter(
    symbol => !selectedAssetList.includes(symbol)
  )
  const AssetInputList = selectedAssetList.map((token, index) => {
    const isUsdc = USDC_TOKEN_LIST.includes(token)
    const balance =
      isUsdc && !isDeposit
        ? freeCollateral
        : (walletMap[token] || {})[dataKey] || 0
    return (
      <AssetInputBlock
        key={token}
        token={token}
        balance={balance}
        amount={resultMap[token]}
        decimalPlaces={wallet[token]?.decimals}
        assetList={assetDropdownList}
        onClose={
          index > 0
            ? () => {
                setSelectedAssetList(prevList => {
                  return prevList.filter(symbol => symbol !== token)
                })
                setResultMap(prevMap => {
                  const map = { ...prevMap }
                  delete map[token]
                  return map
                })
              }
            : null
        }
        onUpdateToken={symbol => {
          setSelectedAssetList(prevList => {
            prevList.splice(index, 1, symbol)
            return prevList
          })
          setResultMap(prevMap => {
            const map = { ...prevMap }
            map[symbol] = map[token]
            delete map[token]
            return map
          })
        }}
        onUpdate={(token, amount) => {
          setResultMap({
            ...resultMap,
            [token]: amount
          })
        }}
      />
    )
  })

  const ResultList = validSelectedAssetList.map(token => (
    <div key={token} className="result-list-row">
      {numberFormat(
        resultMap[token],
        BigNumber(resultMap[token]).gt(1_000_000_000)
          ? {
              configKey: 'max2',
              compact: true
            }
          : 'max8'
      )}
      <div className="token-label">{token}</div>
    </div>
  ))

  return (
    <div
      className={clsx('popup-card-overlay', {
        show: showElement,
        active
      })}
    >
      <div className="popup-card wallet-operation-popup">
        <div className="wallet-operation-popup-title">{operation}</div>
        <div className="wallet-operation-container">
          <div className="asset-input-list">
            {AssetInputList}
            <div
              className={clsx('add-asset-text-button noselect', {
                active: availableAssetAmount > 0
              })}
              onClick={onClickAddAsset}
            >
              You can add{' '}
              <span className="text-highlight">
                {availableAssetAmount} MORE
              </span>{' '}
              {assetText} +
            </div>
          </div>
          <div className="operation-result">
            <div className="operation-result-content">
              <div className="destination-name">to {destinationName}</div>
              <div className="divider-wrap">
                <span className="divider-label">Receive</span>
                <div className="divider" />
              </div>
              <div className="asset-operation-list">
                {ResultList}
                {showFreeUsdcButton && (
                  <div
                    className={clsx('result-list-row', {
                      'is-button': !requestFreeToken
                    })}
                    onClick={() =>
                      !requestFreeToken && setRequestFreeToken(true)
                    }
                  >
                    {requestFreeToken ? (
                      <>
                        1,000
                        <div className="token-label">free USDC.e</div>
                      </>
                    ) : (
                      '🪄 Get free 1,000 USDC.e'
                    )}
                  </div>
                )}
              </div>
              <button
                className={clsx('submit-button', {
                  disabled: !operationValid,
                  loading: isLoading
                })}
                onClick={onSubmit}
              >
                {operation}
              </button>
            </div>
          </div>
        </div>
        <div className="popup-card-close-button" onClick={onPopupClose} />
      </div>
    </div>
  )
}
